// A price staff type for one order line (Jon, 2026-10-16): a manual order's line, or a
// line on the order page, can carry a price for THIS order instead of the menu's. It is
// stored in `order_items.unit_price` with `price_typed = true`, and the QuickBooks send
// uses it ahead of the product's send-time price (shared by the routes and the two
// admin forms, so they refuse the same input). Pure functions only.

export const TYPED_PRICE_MAX = 1_000_000;

export type TypedPriceInput =
  /** The body did not mention a price. */
  | { given: false }
  /** null or "": back to the menu's price. */
  | { given: true; price: null }
  /** A price, as `products.price` is stored: "12.50". */
  | { given: true; price: string }
  | { given: true; error: string };

/**
 * What a request body (or a form box) says about a line's price. A number or a numeric
 * string, more than zero, at most two decimals, at most TYPED_PRICE_MAX; a leading "$"
 * and spaces are ignored. Anything else is an error the caller answers 400 with.
 */
export function parseTypedPrice(raw: unknown): TypedPriceInput {
  if (raw === undefined) return { given: false };
  if (raw === null) return { given: true, price: null };
  const text = typeof raw === "number" ? String(raw) : typeof raw === "string" ? raw.trim().replace(/^\$\s*/, "") : "";
  if (typeof raw === "string" && text === "") return { given: true, price: null };
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return { given: true, error: "Enter a price like 12.50 (two decimals at most)." };
  const value = Number(text);
  if (!(value > 0)) return { given: true, error: "A price is more than zero; clear the box to use the menu price." };
  if (value > TYPED_PRICE_MAX) return { given: true, error: `A price is at most ${TYPED_PRICE_MAX.toLocaleString()}.` };
  return { given: true, price: value.toFixed(2) };
}
